import type { UserMessage } from "@redotech/merchant-sdk/rpc/schema/agents/merchant-support-agent/escalation";
import type {
  HumanMessage,
  IntercomAttachment,
} from "@redotech/redo-merchant-app-common/escalation";
import { sanitizeIntercomBody } from "@redotech/redo-merchant-app-common/escalation";
import type { UIMessage } from "ai";
import type { AskRedoUIMessage } from "../types";

/**
 * Stable id prefixes so the live SSE subscription can dedupe against
 * messages that the initial-load merge already injected (and vice
 * versa). Pick prefixes that won't collide with the AI SDK's
 * `useChat`-generated message ids.
 */
const HUMAN_MESSAGE_ID_PREFIX = "escalation-human-";
const USER_REPLY_MESSAGE_ID_PREFIX = "escalation-user-";

export function isHumanEscalationMessage(
  message: Pick<UIMessage, "id">,
): boolean {
  return message.id.startsWith(HUMAN_MESSAGE_ID_PREFIX);
}

/** Escalation UI shows agents by first name ("Connected with Casey"). */
export function agentDisplayName(
  authorName: string | null | undefined,
): string | null {
  return authorName?.split(" ")[0] ?? null;
}

export function humanMessageId(intercomId: string): string {
  return `${HUMAN_MESSAGE_ID_PREFIX}${intercomId}`;
}

export function userReplyMessageId(intercomId: string): string {
  return `${USER_REPLY_MESSAGE_ID_PREFIX}${intercomId}`;
}

/**
 * True for messages the live escalation watcher injects into the chat
 * (human replies / echoed user replies). Used by the chat-history
 * hydration to tell "the Chat only holds live escalation bubbles, merge
 * the AI transcript in under them" apart from "the Chat already holds a
 * real conversation (streamed or user-typed), leave it alone."
 */
export function isEscalationInjectedMessageId(id: string): boolean {
  return (
    id.startsWith(HUMAN_MESSAGE_ID_PREFIX) ||
    id.startsWith(USER_REPLY_MESSAGE_ID_PREFIX)
  );
}

/**
 * Convert an Intercom human (support agent) reply into an AskRedoUIMessage
 * rendered as an assistant turn. Native `file` parts are used for
 * attachments so the existing renderFilePart path renders them inline.
 */
export function humanMessageToUIMessage(
  message: HumanMessage,
): AskRedoUIMessage {
  return {
    id: humanMessageId(message.id),
    role: "assistant",
    metadata: { agentName: agentDisplayName(message.authorName) },
    parts: buildParts(message.body, message.attachments),
  };
}

/**
 * Convert an Intercom user-reply (echoed back from the server) into an
 * AskRedoUIMessage rendered as a user turn. Mirrors the message the
 * merchant typed during an active escalation.
 */
export function userReplyToUIMessage(message: UserMessage): AskRedoUIMessage {
  return {
    id: userReplyMessageId(message.id),
    role: "user",
    parts: buildParts(message.body, message.attachments),
  };
}

type UIMessageParts = UIMessage["parts"];

function buildParts(
  body: string | null | undefined,
  attachments: IntercomAttachment[] | null | undefined,
): UIMessageParts {
  const parts: UIMessageParts = [];

  const text = sanitizeIntercomBody(body);
  if (text.length > 0) {
    parts.push({ type: "text", text });
  }

  for (const attachment of attachments ?? []) {
    parts.push({
      type: "file",
      url: attachment.url,
      mediaType: attachment.contentType ?? "application/octet-stream",
      filename: attachment.name ?? undefined,
    });
  }

  // Always return at least one part so the renderer doesn't choke on
  // empty assistant turns (e.g. an Intercom note with no body and no
  // attachments). The empty text part is harmless.
  if (parts.length === 0) {
    parts.push({ type: "text", text: "" });
  }

  return parts;
}

/**
 * Append fetched admin messages to existing chat messages, skipping ids
 * already present. Returns the input array when nothing new is added.
 */
export function mergeAdminMessages(
  existing: AskRedoUIMessage[],
  fetched: HumanMessage[],
): AskRedoUIMessage[] {
  const present = new Set(existing.map((m) => m.id));
  const additions = fetched
    .map(humanMessageToUIMessage)
    .filter((ui) => !present.has(ui.id));
  return additions.length === 0 ? existing : [...existing, ...additions];
}

/**
 * Merge stored human + user messages from Intercom into a single
 * chronologically-ordered list of UIMessages. Used on thread reopen.
 */
export function mergeEscalationHistory(
  humanMessages: HumanMessage[],
  userMessages: UserMessage[],
): AskRedoUIMessage[] {
  type Combined =
    | { kind: "human"; createdAt: number; message: HumanMessage }
    | { kind: "user"; createdAt: number; message: UserMessage };

  const combined: Combined[] = [
    ...humanMessages.map((m) => ({
      kind: "human" as const,
      createdAt: m.createdAt,
      message: m,
    })),
    ...userMessages.map((m) => ({
      kind: "user" as const,
      createdAt: m.createdAt,
      message: m,
    })),
  ].sort((a, b) => a.createdAt - b.createdAt);

  return combined.map((entry) =>
    entry.kind === "human"
      ? humanMessageToUIMessage(entry.message)
      : userReplyToUIMessage(entry.message),
  );
}
